// The room switcher: an icon rail replacing the old text tab row for the three
// conversations a plan can hold.
//
// `docs/archived/13-visual-refresh.md`: the icon encodes HOW MANY PEOPLE are in the room, not a
// shared "chat" glyph — a single figure (private), two figures (the co-principals' room), a
// group of three (the full conversation) — which is what lets the text labels drop. Hover
// or focus discloses the room's name in a small flyout beside the icon; the accessible name
// on the link itself carries the same text at every width, screen reader or not.
//
// "Principals" never renders as user-facing text here (`docs/archived/09-web-client.md` and
// `docs/archived/13-visual-refresh.md`'s own acceptance criteria): the co-principals' room is
// labelled with the other principal's name when this client can verify who that is (a
// two-person plan), and a role-neutral phrase otherwise — see `format.ts`'s
// `namedCoPrincipal` and `PlanScreen.tsx`'s `principalsRoomLabel` for why.

import { NavLink } from 'react-router-dom';

import type { Thread } from '../api/contract.js';
import { GroupIcon, PrivateIcon, TwoIcon } from './RoomIcons.js';

function UnreadDot({ thread }: { thread: Thread | null }): JSX.Element | null {
  if (thread?.unread !== true) {
    return null;
  }
  return (
    <span className="tabs__badge roomSwitcher__dot" aria-hidden="true">
      •
    </span>
  );
}

type RoomLinkProps = {
  to: string;
  active: boolean;
  label: string;
  thread: Thread | null;
  children: JSX.Element;
  end?: boolean;
};

function RoomLink({
  to,
  active,
  label,
  thread,
  children,
  end = false,
}: RoomLinkProps): JSX.Element {
  return (
    <li className="roomSwitcher__item">
      <NavLink
        className={active ? 'roomSwitcher__button active' : 'roomSwitcher__button'}
        to={to}
        end={end}
        aria-label={label}
      >
        {children}
        <UnreadDot thread={thread} />
      </NavLink>
      <span className="roomSwitcher__flyout" aria-hidden="true">
        {label}
      </span>
    </li>
  );
}

export type RoomSwitcherProps = {
  base: string;
  landing: 'private' | 'principals' | 'conversation';
  ownThread: Thread | null;
  principalsThread: Thread | null;
  groupThread: Thread | null;
  /** "Erica" when this client can verify who the co-principal is, "your co-principals"
   * otherwise — see the header comment above. */
  principalsRoomLabel: string;
};

export function RoomSwitcher({
  base,
  landing,
  ownThread,
  principalsThread,
  groupThread,
  principalsRoomLabel,
}: RoomSwitcherProps): JSX.Element {
  return (
    <nav className="roomSwitcher" aria-label="This plan">
      <ul className="roomSwitcher__list">
        <RoomLink
          to={`${base}/private`}
          active={landing === 'private'}
          label="Private chat"
          thread={ownThread}
        >
          <PrivateIcon />
        </RoomLink>
        {principalsThread === null ? null : (
          <RoomLink
            to={`${base}/principals`}
            active={landing === 'principals'}
            label={principalsRoomLabel}
            thread={principalsThread}
          >
            <TwoIcon />
          </RoomLink>
        )}
        {groupThread === null ? null : (
          <RoomLink
            to={base}
            end
            active={landing === 'conversation'}
            label="Everyone"
            thread={groupThread}
          >
            <GroupIcon />
          </RoomLink>
        )}
      </ul>
    </nav>
  );
}
